/**
 * Sidebar Menu Components
 * A responsive sidebar navigation system with both desktop and mobile layouts
 * Features animated transitions, active state tracking, and environment indicators
 */

import React, { useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  XMarkIcon,
  CogIcon,
  UsersIcon,
  PaintBrushIcon,
} from "@heroicons/react/24/solid";

/**
 * SidebarMenu Component
 * Provides navigation controls through a collapsible sidebar interface
 *
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Controls sidebar visibility on mobile devices
 *
 * Features:
 * - Responsive desktop and mobile layouts
 * - Animated transitions
 * - Active route highlighting
 * - Environment indicator for non-production environments
 *
 * @returns {JSX.Element} A sidebar navigation menu with responsive behavior
 */
function SidebarMenu({ isOpen }) {
  const sidebarRef = useRef(null);
  const location = useLocation();

  /**
   * Navigation menu configuration
   * Defines available routes and their visual properties
   * @type {Array<{path: string, icon: Component, text: string}>}
   */
  const menuItems = [
    {
      path: "configurations",
      icon: CogIcon,
      text: "Configurazioni piattaforma",
    },
    {
      path: "theme-showcase",
      icon: PaintBrushIcon,
      text: "Showcase Tema",
    },
    {
      path: "user-permissions",
      icon: UsersIcon,
      text: "Permessi utente",
    },
    {
      path: "roles",
      icon: UsersIcon,
      text: "Ruoli",
    },
    {
      path: "user-roles",
      icon: UsersIcon,
      text: "Ruoli Utente",
    },
    {
      path: "client-permissions",
      icon: UsersIcon,
      text: "Permessi client",
    },
    {
      path: "manage-users",
      icon: UsersIcon,
      text: "Gestione utenze",
    },
  ];

  /**
   * Determines if a given path matches the current route
   * @param {string} path - Route path to check
   * @returns {boolean} True if path matches current route
   */
  const isActivePath = (path) => {
    const currentPath = location.pathname.split("/").pop();
    return currentPath === path;
  };

  /**
   * Renders navigation menu items with active state styling
   * @returns {JSX.Element} Navigation menu with styled links
   */
  const renderMenuItems = () => (
    <nav className="mt-11">
      {menuItems.map(({ path, icon: Icon, text }) => {
        const isActive = isActivePath(path);
        return (
          <Link
            key={path}
            to={path}
            className={`flex items-center mb-2 p-2 rounded transition-colors duration-300 
             ${
               isActive
                 ? "bg-blue-50 text-blue-700 font-semibold border-l-4 border-blue-700"
                 : "hover:bg-gray-100 text-gray-700"
             }`}
          >
            <Icon
              className={`w-5 h-5 mr-2 ${
                isActive ? "text-blue-700" : "text-gray-500"
              }`}
            />
            {text}
          </Link>
        );
      })}
    </nav>
  );

  return (
    <>
      {/* Desktop sidebar */}
      <div
        className="hidden md:block w-64 p-4 min-h-screen 
             backdrop-blur-lg bg-opacity-70 border-r border-sidebar-border"
      >
        <div className="text-lg font-bold mb-4">
          <h2>Impostazioni</h2>
          {process.env.REACT_APP_ENV !== "prod" && (
            <h2>Env: {process.env.REACT_APP_ENV}</h2>
          )}
        </div>
        {renderMenuItems()}
      </div>

      {/* Mobile sidebar */}
      <div
        ref={sidebarRef}
        className={`md:hidden fixed top-0 left-0 p-2 min-h-screen w-72
               backdrop-blur-lg bg-opacity-70 z-50 transform transition-transform duration-300 ease-in-out 
               border-r border-sidebar-border ${isOpen ? "translate-x-0" : "-translate-x-full"}`}
      >
        <div className="text-lg font-bold mb-4">
          <h2>Menu</h2>
        </div>
        {renderMenuItems()}
      </div>

      {/* Mobile close button */}
      <div
        className={`md:hidden fixed top-2 z-50 transition-all duration-300 ease-in-out transform 
               ${isOpen ? "translate-x-64 ms-0" : "translate-x-0 ms-3"}`}
      >
        <button>{isOpen && <XMarkIcon className="w-6 h-6" />}</button>
      </div>
    </>
  );
}

export default SidebarMenu;
